import React from "react";
import { PrintOutlined } from "@mui/icons-material";
import {
    Box,
    Button,
    CircularProgress,
    Typography,
} from "@mui/material";
import AppTable from "components/Shared/AppTable";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import { useLazyListVendorPendingPaymentsPrintQuery } from "store/services/reportApi";
import { handleVendorPendingPaymentPrint } from "screens/App/CRM/Reports/PaymentReports/VendorReports/HandleVendorPendingPaymentPrint";
import { Link, useSearchParams } from "react-router-dom";
import HighlightedText from "components/Shared/HighlightedText";

const VendorPendingChargeList = ({ vendorId }) => {

    const [totalChargeAmount, setTotalChargeAmount] = React.useState(0);

    const [searchParams] = useSearchParams();
    const keyword = searchParams.get("search") || "";

    const [fetchVendorPendingPayments, { data: pendingPaymentsRes, isLoading, isFetching }] =
        useLazyListVendorPendingPaymentsPrintQuery();

    // Fetch pending Charge data
    React.useEffect(() => {
        if (!vendorId) return;

        const fetchData = async () => {
            try {
                const result = await fetchVendorPendingPayments({ vendorId });
                if (result.data) {
                    setTotalChargeAmount(result.data.totalChargeAmount || 0);
                }
            } catch (error) {
                console.error("Failed to fetch pending Charge:", error);
            }
        };

        fetchData();
    }, [vendorId, fetchVendorPendingPayments]);

    const handlePrintClick = async () => {
        if (!vendorId) return;

        const res = await handleVendorPendingPaymentPrint(
            vendorId,
            fetchVendorPendingPayments,
            "charge"
        );

        setTotalChargeAmount(res?.totalChargeAmount || 0);
    };

    const columns = [
        {
            name: "SN",
            selector: (row, index) => index + 1,
            cell: (row, index) => index + 1,
            width: "80px",
        },
        {
            name: "Shipment ID",
            selector: (row) => row?.shipmentId,
            cell: (row) => (
                <Link
                    to={`/app/crm/shipment/${row._id}/detail?tab=pricing`}
                    className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
                >
                    {row?.shipmentId ? (
                        <HighlightedText text={row.shipmentId} highlight={keyword} />
                    ) : "N/A"}
                </Link>
            ),
        },
        {
            name: "Shipment Status",
            selector: (row) => row?.shipmentStatus,
            cell: (row) => row?.shipmentStatus || "N/A",
        },
        {
            name: "Charge Amount",
            selector: (row) => row?.chargeAmount,
            cell: (row) => `Rs ${row?.chargeAmount?.toFixed(2) || 0}`,
        },
        {
            name: "Payment Status",
            selector: (row) => row?.paymentStatus,
            cell: (row) => row?.paymentStatus || "N/A",
        },
        {
            name: "Created Date",
            selector: (row) => row?.createdAt,
            cell: (row) => {
                if (!row?.createdAt) return "N/A";
                return new Date(row.createdAt).toLocaleDateString();
            },
        },
    ];

    const pendingPayments = (pendingPaymentsRes?.data || []).filter(
        (item) => item.chargeAmount > 0
    );

    return (
        <BreadcrumbLayout.Paper>
            <Box display="flex" justifyContent="flex-end" m={2}>
                <Button
                    variant="contained"
                    startIcon={
                        isLoading ? <CircularProgress size={18} /> : <PrintOutlined />
                    }
                    onClick={handlePrintClick}
                    disabled={isLoading}
                >
                    Print Pending Charge Rs {totalChargeAmount.toFixed(2)}
                </Button>
            </Box>

            {pendingPaymentsRes ? (
                <AppTable
                    columns={columns}
                    data={pendingPayments}
                    progressPending={isLoading || isFetching}
                    paginationServer={false}
                />
            ) : (
                <Box display="flex" alignItems="center" justifyContent="center" p={2}>
                    <CircularProgress size={24} />
                    <Typography ml={1}>Loading Pending Charge…</Typography>
                </Box>
            )}
        </BreadcrumbLayout.Paper>
    );
};

export default VendorPendingChargeList;